import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import zhCN from './locales/zh-CN.json'

/**
 * 国际化配置。
 *
 * 当前只启用简体中文，但全站的语言相关行为都收敛到本文件的 LOCALES 表中，
 * 新增语言时不需要在业务代码里找散落的 'zh-CN' 硬编码。
 *
 * 新增一门语言的完整步骤：
 *   1. 新增 locales/xx-XX.json（key 与 zh-CN.json 对齐，可用 npm run i18n:check 校验）
 *   2. 新增 @/content/articles/xx-XX.ts（资讯正文，见该目录说明）
 *   3. 在下方 LOCALES 里加一项，并在 localeLoaders 注册懒加载
 * 语言切换入口会在 LOCALES 多于一项时自动出现。
 */

/** 数字缩写习惯：wan = 中文「万」制；western = K/M 制 */
export type NumberStyle = 'wan' | 'western'

export interface LocaleMeta {
  /** i18next 语言码，同时用作 Accept-Language 请求头 */
  code: string
  /** 语言切换器里显示的自称 */
  nativeLabel: string
  /** Intl.DateTimeFormat / toLocaleDateString 使用的 locale */
  dateLocale: string
  /** 大数字缩写习惯 */
  numberStyle: NumberStyle
}

export const LOCALES: LocaleMeta[] = [
  {
    code: 'zh-CN',
    nativeLabel: '简体中文',
    dateLocale: 'zh-CN',
    numberStyle: 'wan',
  },
]

export const DEFAULT_LANGUAGE = LOCALES[0].code

/** 是否需要展示语言切换入口（只有一门语言时隐藏） */
export const MULTI_LOCALE = LOCALES.length > 1

/** 取当前语言的元数据，未知语言回落到默认语言 */
export function currentLocale(): LocaleMeta {
  return LOCALES.find((l) => l.code === i18n.language) ?? LOCALES[0]
}

/** 非默认语言的语言包懒加载器；新增语言时在此登记 */
const localeLoaders: Record<string, () => Promise<{ default: Record<string, string> }>> = {}

export async function changeLanguage(code: string) {
  if (!LOCALES.some((l) => l.code === code)) return
  if (code !== DEFAULT_LANGUAGE && localeLoaders[code] && !i18n.hasResourceBundle(code, 'translation')) {
    try {
      const mod = await localeLoaders[code]()
      i18n.addResourceBundle(code, 'translation', mod.default, true, true)
    } catch (e) {
      console.warn(`加载语言包失败: ${code}`, e)
      return
    }
  }
  await i18n.changeLanguage(code)
}

i18n.use(initReactI18next).init({
  resources: { 'zh-CN': { translation: zhCN } },
  lng: DEFAULT_LANGUAGE,
  fallbackLng: DEFAULT_LANGUAGE,
  interpolation: { escapeValue: false },
  react: { useSuspense: false },
})

export default i18n
